import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useIdentityClient } from "@packsize/authentication";

import { getServerSettings, type ServerSettings, updateServerSettings } from "@/api/server-settings";

const SERVER_SETTINGS_QUERY_KEY = ["server-settings"] as const;

type UseServerSettingsOptions = Omit<
	UseQueryOptions<ServerSettings | undefined, Error, ServerSettings | undefined, readonly string[]>,
	"queryKey" | "queryFn"
>;

export const useServerSettings = (queryConfig: UseServerSettingsOptions = {}) => {
	const { client } = useIdentityClient();
	const queryFn = () => getServerSettings(client);

	const query = useQuery({
		queryKey: SERVER_SETTINGS_QUERY_KEY,
		queryFn,
		...queryConfig,
	});

	return {
		data: query.data,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	};
};

export const useUpdateServerSettings = () => {
	const { client } = useIdentityClient();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (settings: ServerSettings) => updateServerSettings(client, settings),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SERVER_SETTINGS_QUERY_KEY });
		},
	});

	return {
		mutate: mutation.mutateAsync,
		isPending: mutation.isPending,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		error: mutation.error,
	};
};
